/**
 * Handles circle drag end operation
 * @param {string} id - Circle ID
 * @param {number} x - New x position
 * @param {number} y - New y position
 * @param {Array} circles - Current circles array
 * @param {Function} setCircles - Function to set circles
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @param {Object} currentCircle - Optional: pass current circle object to preserve all properties (rotation, scaleX, scaleY, etc.)
 */
export const handleCircleDragEnd = (
  id,
  x,
  y,
  circles,
  setCircles,
  saveToHistory,
  isUndoingRedoing,
  currentCircle = null, // Optional: pass current circle object to preserve all properties (rotation, scaleX, scaleY, etc.)
  isOffCanvas = false // Optional: indicate if this is an off-canvas circle
) => {
  // Safety check: ensure circles is an array
  if (!Array.isArray(circles)) {
    return;
  }

  // CRITICAL: Use functional update to get the current state, avoiding stale closure issues
  // This ensures we always work with the most up-to-date circles array
  setCircles((prevCircles) => {
    // CRITICAL: Find the current circle to preserve all properties
    // If currentCircle is provided, use it; otherwise find it from the current state
    // This ensures we preserve rotation, scaleX, scaleY, and all other properties
    const existingCircle = currentCircle || prevCircles.find((circle) => circle.id === id);
    
    if (!existingCircle) {
      return prevCircles; // Return unchanged if circle not found
    }

    // CRITICAL: Preserve ALL properties from the existing circle (rotation, scaleX, scaleY, etc.)
    // Only update x, y - everything else should be preserved
    const updatedCircles = prevCircles.map((circle) =>
      circle.id === id
        ? {
            ...existingCircle, // Preserve all existing properties (rotation, scaleX, scaleY, etc.)
            x,
            y,
            _isInOffCanvasDuringDrag: undefined,
            _previewTargetStage: undefined,
            _isDragging: undefined,
          }
        : circle
    );
    
    // Debug logging to verify rotation is preserved
    const updatedCircle = updatedCircles.find((circle) => circle.id === id);
    if (updatedCircle) {
    }

    // Save to history with the updated circles
    // CRITICAL: Call saveToHistory OUTSIDE the setCircles callback to avoid React batching issues
    // Use setTimeout to ensure it's called after the state update completes
    if (saveToHistory) {
      // Check if we're currently undoing/redoing to prevent clearing redo stack
      if (isUndoingRedoing && isUndoingRedoing.current) {
        return updatedCircles;
      }

      // CRITICAL: Save drag operations immediately, not when element is unselected
      // This ensures drag operations create separate history entries that can be undone independently
      // Pass the updated circles array as override to ensure saveToHistory gets the correct state
      // saveToHistory signature: (operationType, currentImagesOverride, currentArrowsOverride, currentDoubleSidedArrowsOverride, currentLinesOverride, currentTextsOverride, currentSelectedTextOverride, currentSelectedTextIdOverride, currentRectanglesOverride, currentOffCanvasRectanglesOverride, currentCirclesOverride, currentOffCanvasCirclesOverride, ...)
      // Index 10 is currentCirclesOverride, index 11 is currentOffCanvasCirclesOverride
      setTimeout(() => {
        if (isOffCanvas) {
          saveToHistory("circle_drag_end", null, null, null, null, null, null, null, null, null, null, updatedCircles);
        } else {
          saveToHistory("circle_drag_end", null, null, null, null, null, null, null, null, null, updatedCircles);
        }
      }, 0);
    }

    return updatedCircles;
  });
};

/**
 * Create a function to handle circle drag end with bound dependencies
 * @param {Array} circles - Current circles array
 * @param {Function} setCircles - Function to set circles
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @returns {Function} Function to handle circle drag end
 */
// Track last drag end call to prevent duplicates
let lastCircleDragEndCall = null;
const CIRCLE_DRAG_END_DEBOUNCE_MS = 50; // 50ms debounce window

export const createHandleCircleDragEnd = (
  circles,
  setCircles,
  saveToHistory,
  isUndoingRedoing
) => {
  return (id, x, y, currentCircle = null, isOffCanvas = false) => {
    // CRITICAL: Debounce duplicate calls to prevent creating duplicate history entries
    // This handles cases where handleCircleDragEnd is called twice (e.g., from React strict mode or multiple event handlers)
    const now = Date.now();
    const callKey = `${id}-${x}-${y}`;
    
    if (
      lastCircleDragEndCall &&
      lastCircleDragEndCall.key === callKey &&
      now - lastCircleDragEndCall.timestamp < CIRCLE_DRAG_END_DEBOUNCE_MS
    ) {
      return; // Skip duplicate call
    }
    
    // Update last call tracking
    lastCircleDragEndCall = {
      key: callKey,
      timestamp: now,
    };
    
    return handleCircleDragEnd(
      id,
      x,
      y,
      circles,
      setCircles,
      saveToHistory,
      isUndoingRedoing,
      currentCircle, // Pass the current circle object to preserve all properties (rotation, scaleX, scaleY, etc.)
      isOffCanvas // Pass isOffCanvas flag to handle main vs off-canvas circles
    );
  };
};

